import React, { useContext, useState, useEffect, Suspense } from 'react';
import { auth } from '../config/firebaseConfig';
import { AuthContext, waitForAuthFlow } from '../context/AuthContext';
import LoadingIndicator from "../components/LoadingIndicator";
import { useLocation, useNavigate } from 'react-router';
import { TestAdminUid } from "../config/keys";
import cache from '../config/apolloCache';
import { activateApolloCacheForUser } from '../config/apolloCachePersistence';
import {
    setActiveNotificationVenue,
    canUseVenueNotifications,
    registerVenueNotifications,
    requestAndRegisterVenueNotifications,
} from '../services/venueNotificationService';
import VenueAvailabilityBanner from '../components/VenueAvailabilityBanner';
import client from '../config/apolloClient';
import { GET_CLUB_BY_EMAIL } from '../graphql/clubModel/ClubQuery';
import { requiresVenueEmailVerification } from '../config/authPolicy';
import logger from '../utils/logger';
import WebMaintenanceGate from '../components/WebMaintenanceGate';
import VerifyIntakeEmail from '../screens/VerifyIntakeEmail';

// Lazy load stacks
const AuthStack = React.lazy(() => import('./AuthStack'));
const MainStack = React.lazy(() => import('./MainStack'));
const AdminStack = React.lazy(() => import('./AdminStack'));

const Routes = () => {
    const { club, setClub, setClubDetails } = useContext(AuthContext);
    const [initializing, setInitializing] = useState(true);
    const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
    const [enablingNotifications, setEnablingNotifications] = useState(false);
    const [webNotification, setWebNotification] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        logger.info('navigation.route_changed', { path: location.pathname });
    }, [location.pathname]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
            const startedAt = Date.now();
            logger.info('auth.session_validation_started', { authenticated: Boolean(firebaseUser) });
            try {
                await waitForAuthFlow();
                if (!firebaseUser || auth.currentUser?.uid !== firebaseUser.uid) {
                    await activateApolloCacheForUser(cache, undefined);
                    setClubDetails(null);
                    setClub(null);
                    logger.info('auth.session_cleared', { reason: 'signed_out' });
                    return;
                }

                if (firebaseUser.uid === TestAdminUid) {
                    await activateApolloCacheForUser(cache, firebaseUser.uid);
                    setClub(firebaseUser);
                    logger.info('auth.session_validation_completed', { role: 'admin', durationMs: Date.now() - startedAt });
                    return;
                }

                if (!firebaseUser.email || (
                    requiresVenueEmailVerification && !firebaseUser.emailVerified
                )) {
                    await auth.signOut();
                    await activateApolloCacheForUser(cache, undefined);
                    setClubDetails(null);
                    setClub(null);
                    logger.warn('auth.session_rejected', { reason: 'missing_or_unverified_email' });
                    return;
                }

                await firebaseUser.getIdToken(true);
                const { data } = await client.query({
                    query: GET_CLUB_BY_EMAIL,
                    variables: { email: firebaseUser.email },
                    fetchPolicy: 'network-only',
                });
                const registeredClub = data?.getClubByEmail;
                if (!registeredClub?.id) {
                    await auth.signOut();
                    await activateApolloCacheForUser(cache, undefined);
                    setClubDetails(null);
                    setClub(null);
                    logger.warn('auth.session_rejected', { reason: 'venue_registration_incomplete' });
                    alert('Venue signup is not complete. Finish registration before signing in.');
                    return;
                }

                await activateApolloCacheForUser(cache, firebaseUser.uid);
                setClubDetails(registeredClub);
                setClub(firebaseUser);
                logger.info('auth.session_validation_completed', { role: 'venue', durationMs: Date.now() - startedAt });
            } catch (error) {
                await auth.signOut().catch(() => {});
                await activateApolloCacheForUser(cache, undefined);
                setClubDetails(null);
                setClub(null);
                logger.error('auth.session_validation_failed', { durationMs: Date.now() - startedAt, error });
            } finally {
                setInitializing(false);
            }
        });
        return unsubscribe; // unsubscribe on unmount
    }, [setClub, setClubDetails]);

    // After initialization, if authenticated but still on an auth path, normalize to /Home once.
    useEffect(() => {
        if (!initializing && club) {
            const path = location.pathname || '/';
            if (path.toLowerCase() === '/signup' || path === '/') {
                navigate('/Home', { replace: true });
            }
        }
    }, [initializing, club, location.pathname, navigate]);

    useEffect(() => {
        let active = true;
        setActiveNotificationVenue(club?.email);
        const prepareNotifications = async () => {
            if (!club?.email || club.uid === TestAdminUid || !(await canUseVenueNotifications())) {
                if (active) setShowNotificationPrompt(false);
                return;
            }
            if (Notification.permission === 'granted') {
                try {
                    await registerVenueNotifications(club.email);
                } catch (error) {
                    console.warn('Venue notifications could not be registered', error);
                }
                return;
            }
            if (active) setShowNotificationPrompt(Notification.permission === 'default');
        };
        prepareNotifications();
        return () => { active = false; };
    }, [club]);

    useEffect(() => {
        let timeout;
        const showWebNotification = event => {
            setWebNotification(event.detail);
            clearTimeout(timeout);
            timeout = setTimeout(() => setWebNotification(null), 8000);
        };
        window.addEventListener('boozeon:notification', showWebNotification);
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('boozeon:notification', showWebNotification);
        };
    }, []);

    const enableNotifications = async () => {
        setEnablingNotifications(true);
        try {
            const enabled = await requestAndRegisterVenueNotifications(club?.email);
            setShowNotificationPrompt(!enabled && Notification.permission === 'default');
        } catch (error) {
            console.warn('Venue notifications could not be enabled', error);
        } finally {
            setEnablingNotifications(false);
        }
    };

    if (initializing) {
        return <LoadingIndicator />; // or splash screen
    }

    if (location.pathname === '/VerifyIntakeEmail') {
        return <VerifyIntakeEmail signedIn={Boolean(club)} />;
    }

    return (
        <>
            {showNotificationPrompt && (
                <div role="status" style={{
                    position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
                    zIndex: 2000, background: '#1f2937', color: '#fff', padding: '12px 16px',
                    borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.25)',
                    display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    <span>Enable notifications for peg inventory updates.</span>
                    <button
                        type="button"
                        onClick={enableNotifications}
                        disabled={enablingNotifications}
                        style={{ border: 0, borderRadius: 6, padding: '8px 12px', cursor: 'pointer' }}
                    >
                        {enablingNotifications ? 'Enabling…' : 'Enable'}
                    </button>
                </div>
            )}
            {webNotification && (
                <div role="alert" style={{
                    position: 'fixed', top: 12, right: 12, zIndex: 2100,
                    width: 'min(380px, calc(100vw - 24px))', background: '#211c10', color: '#fff',
                    padding: '14px 16px', border: '1px solid rgba(255, 218, 78, .38)',
                    borderLeft: '5px solid #ffda4e', borderRadius: 8,
                    boxShadow: '0 6px 24px rgba(0,0,0,.25)',
                }}>
                    <div style={{
                        color: '#ffda4e', fontSize: 11, fontWeight: 800,
                        letterSpacing: '.08em', marginBottom: 5, textTransform: 'uppercase',
                    }}>Venue update</div>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{webNotification.title}</div>
                    <div style={{ color: 'rgba(255,255,255,.84)', lineHeight: 1.45 }}>
                        {webNotification.body}
                    </div>
                </div>
            )}
            <Suspense fallback={<LoadingIndicator />}>
                {club?.uid === TestAdminUid ? (
                    <AdminStack />
                ) : club ? (
                    <WebMaintenanceGate>
                        <VenueAvailabilityBanner />
                        <MainStack />
                    </WebMaintenanceGate>
                ) : (
                    <AuthStack />
                )}
            </Suspense>
        </>
    );
};

export default Routes;
